import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ShieldAlert,
  ArrowLeft,
  Home,
  Mail,
  Compass,
  Coffee,
  Wifi,
  Hourglass,
  Zap,
  Ghost,
  KeyRound,
  ServerCrash,
  TriangleAlert,
  Construction,
} from "lucide-react";

/**
 * Avida-branded witty error page — drop-in fallback for any HTTP error.
 *
 * Usage:
 *   <ErrorPage code={404} />
 *   <ErrorPage code={500} reason={stackTrace} onReset={fn} />
 *   <Route path="/error/:code" element={<ErrorPage />} />
 *   <Route path="*" element={<ErrorPage code={404} />} />
 */

const ERRORS = {
  400: {
    label: "Bad Request",
    icon: TriangleAlert,
    headline: "We couldn't process that request.",
    quip: "Some of the information didn't come through correctly. Please check the fields and try again.",
  },
  401: {
    label: "Unauthorized",
    icon: KeyRound,
    headline: "Please sign in to continue.",
    quip: "Your session isn't active. Sign in again to access this page.",
  },
  403: {
    label: "Forbidden",
    icon: ShieldAlert,
    headline: "You don't have access to this page.",
    quip: "Your account doesn't have permission to view this. If you believe this is a mistake, contact your administrator.",
  },
  404: {
    label: "Not Found",
    icon: Ghost,
    headline: "We couldn't find that page.",
    quip: "The page you're looking for may have moved or no longer exists. Use the options below to get back on track.",
  },
  408: {
    label: "Request Timeout",
    icon: Hourglass,
    headline: "The request timed out.",
    quip: "The server took too long to respond. Please refresh and try again.",
  },
  418: {
    label: "I'm a Teapot",
    icon: Coffee,
    headline: "This request can't be completed.",
    quip: "The server can't fulfill this request. Please try again or contact support.",
  },
  429: {
    label: "Too Many Requests",
    icon: Zap,
    headline: "Too many requests.",
    quip: "You've made a lot of requests in a short time. Please wait a few seconds and try again.",
  },
  500: {
    label: "Internal Server Error",
    icon: ServerCrash,
    headline: "Something went wrong on our end.",
    quip: "We've logged the issue and are working on it. Please reload — if the problem continues, contact support.",
  },
  502: {
    label: "Bad Gateway",
    icon: Wifi,
    headline: "We're having trouble reaching the server.",
    quip: "This is usually temporary. Please wait a moment and try again.",
  },
  503: {
    label: "Service Unavailable",
    icon: Construction,
    headline: "The service is temporarily unavailable.",
    quip: "We're performing maintenance or recovering from a brief interruption. Please try again shortly.",
  },
  504: {
    label: "Gateway Timeout",
    icon: Hourglass,
    headline: "The server didn't respond in time.",
    quip: "The upstream service took too long to answer. Please try again shortly.",
  },
};

const FALLBACK = {
  label: "Unexpected Error",
  icon: TriangleAlert,
  headline: "Something went wrong.",
  quip: "An unexpected error occurred. Please use the options below or contact support if it continues.",
};

export default function ErrorPage({
  code: codeProp,
  title,
  message,
  reason = "",
  onReset,
  showHomeButton = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Resolve the error code from prop > URL param > default 404
  const rawCode = codeProp ?? params?.code ?? 404;
  const code = Number(rawCode) || 404;
  const meta = ERRORS[code] || FALLBACK;
  const Icon = meta.icon;

  const handleBack = () => {
    if (onReset) onReset();
    navigate(-1);
  };

  const handleHome = () => {
    if (onReset) onReset();
    // The session rides in an httpOnly cookie (JS can't read it), so send
    // everyone to the dashboard — the route guard redirects to /login if the
    // session isn't valid.
    navigate("/dashboard");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #0A3B8F 0%, #032866 55%, #021A4A 100%)",
      }}
      data-testid={`error-page-${code}`}
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,218,0,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,218,0,0.4) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Floating glow */}
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-30 animate-pulse"
        style={{ background: "#FFDA00" }}
      />
      <div
        className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-20"
        style={{ background: "#0A3B8F" }}
      />

      <div className="relative max-w-2xl w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-10 text-white shadow-2xl">
        {/* Giant status code */}
        <div className="text-center mb-4">
          <div
            className="leading-none tracking-tighter font-black select-none"
            style={{
              fontSize: "clamp(8rem, 22vw, 18rem)",
              color: "#FFDA00",
              textShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }}
            aria-hidden="true"
            data-testid="error-code"
          >
            {code}
          </div>
        </div>

        <div className="flex items-start gap-5">
          <div
            className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #FFDA00 0%, #F4A500 100%)",
            }}
          >
            <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#032866]" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[11px] font-bold tracking-[0.3em] uppercase"
              style={{ color: "#FFDA00" }}
              data-testid="error-label"
            >
              {meta.label}
            </div>
            <h1
              className="text-2xl sm:text-4xl font-bold mt-1 leading-tight"
              data-testid="error-title"
            >
              {title || meta.headline}
            </h1>
            <p
              className="text-white/70 mt-4 text-base leading-relaxed"
              data-testid="error-message"
            >
              {message || meta.quip}
            </p>

            {/* Path hint — handy for 404s */}
            {code === 404 && location?.pathname && (
              <div
                className="mt-4 p-3 rounded-xl bg-black/20 border border-white/10 text-[12px] font-mono text-white/60 break-words"
                data-testid="error-path"
              >
                <span className="text-white/40">Requested path:</span>{" "}
                {location.pathname}
              </div>
            )}

            {reason && (
              <div
                className="mt-4 p-3 rounded-xl bg-black/20 border border-white/10 text-[12px] font-mono text-white/60 break-words max-h-40 overflow-auto"
                data-testid="error-reason"
              >
                {reason}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <Button
            onClick={handleBack}
            variant="outline"
            className="h-10 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            data-testid="error-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Go back
          </Button>

          {showHomeButton && (
            <Button
              onClick={handleHome}
              className="h-10 rounded-xl text-[#032866] font-semibold hover:opacity-90"
              style={{ background: "#FFDA00" }}
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4 mr-2" /> Avida home
            </Button>
          )}

          {code >= 500 && (
            <Button
              onClick={() => {
                if (onReset) onReset();
                window.location.reload();
              }}
              variant="outline"
              className="h-10 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              data-testid="error-reload-btn"
            >
              <Compass className="w-4 h-4 mr-2" /> Reload
            </Button>
          )}

          <a
            href="mailto:support@avidahealth.org"
            className="inline-flex items-center h-10 px-4 rounded-xl text-[12px] text-white/70 hover:text-white"
            data-testid="error-contact-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> Contact support
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between text-[11px] text-white/50 gap-3 flex-wrap">
          <span>
            <span
              style={{ color: "#FFDA00" }}
              className="font-bold tracking-widest"
            >
              AVIDA HEALTH
            </span>
            <span className="ml-1">· HIPAA-grade platform</span>
          </span>
          <span className="font-mono">
            HTTP {code} · {meta.label}
          </span>
        </div>
      </div>
    </div>
  );
}
